import { useEffect, useState } from "react";

/** How long the pulse runs. Keep in step with `.status-pill-pulse` in index.css. */
export const STATUS_PULSE_DURATION_MS = 300;

/**
 * True for about 0.3s after `status` changes under a component that stays
 * mounted — an issue row whose update just posted and whose list refetched. Add
 * the `status-pill-pulse` class (index.css) to the pill while it is true. See
 * [[issue-item.spec]] US-005.
 *
 * A row that has just mounted never pulses: it has seen no change, only a
 * status. The motion itself is off under `prefers-reduced-motion` in the CSS.
 */
export function useStatusChangePulse(status: string): boolean {
  const [seen, setSeen] = useState(status);
  const [pulsing, setPulsing] = useState(false);

  // Derived during render rather than in an effect, so the pill's first frame
  // with the new status already carries the class — an effect would paint the
  // new status plain and pulse it a frame late.
  if (seen !== status) {
    setSeen(status);
    setPulsing(true);
  }

  useEffect(() => {
    if (!pulsing) return;
    // `status` restarts the timer, so a second change inside the pulse ends
    // 0.3s after the latest one and the pill never goes plain mid-motion.
    const timer = setTimeout(() => setPulsing(false), STATUS_PULSE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [pulsing, status]);

  return pulsing;
}
